<template>
  <div class="crm-page" :class="{ 'crm-page--mobile': isMobile }">
    <!-- header -->
    <header class="crm-head">
      <div class="crm-head__brand">
        <span class="crm-head__logo"><v-icon color="primary">mdi-handshake-outline</v-icon></span>
        <div>
          <div class="crm-head__title">{{ $t('crmTitle') }}</div>
          <v-menu v-if="crm.workspaces.length" offset-y>
            <template #activator="{ on }">
              <button type="button" class="crm-head__ws" v-on="on">
                <span class="crm-head__ws-dot" :style="{ background: (workspace && workspace.color) || '#2166d1' }" />
                {{ workspace ? workspace.name : '' }}
                <v-icon x-small>mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list dense>
              <v-subheader>{{ $t('crmWorkspaces') }}</v-subheader>
              <v-list-item v-for="w in crm.workspaces" :key="w.id" :input-value="workspace && w.id === workspace.id" @click="onSwitch(w)">
                <span class="crm-head__ws-dot me-2" :style="{ background: w.color || '#2166d1' }" />
                <v-list-item-content>
                  <v-list-item-title>{{ w.name }}</v-list-item-title>
                  <v-list-item-subtitle v-if="w.my_role">{{ w.my_role.name || w.my_role }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <nav v-if="crm.ready" class="crm-head__tabs" role="tablist">
        <router-link
          v-for="t in tabs"
          :key="t.key"
          :to="{ name: t.route }"
          class="crm-head__tab"
          :class="{ on: section === t.key }"
          role="tab"
        >
          <v-icon small>{{ t.icon }}</v-icon>{{ t.label }}
        </router-link>
      </nav>

      <v-spacer />

      <v-menu v-if="crm.ready" offset-y left>
        <template #activator="{ on }">
          <v-btn depressed color="primary" class="crm-head__add" v-on="on">
            <v-icon small :class="{ 'me-1': !isMobile }">mdi-plus</v-icon><span v-if="!isMobile">{{ $t('crmQuickAdd') }}</span>
          </v-btn>
        </template>
        <v-list dense class="crm-head__addmenu">
          <v-list-item v-if="can('leads.create')" @click="newLead()"><v-icon small class="me-2">mdi-account-plus-outline</v-icon>{{ $t('crmNewLead') }}<v-spacer /><kbd>N</kbd></v-list-item>
          <v-list-item v-if="can('deals.create')" @click="newDeal()"><v-icon small class="me-2">mdi-briefcase-plus-outline</v-icon>{{ $t('crmNewDeal') }}<v-spacer /><kbd>D</kbd></v-list-item>
        </v-list>
      </v-menu>
    </header>

    <!-- body -->
    <main class="crm-body">
      <div v-if="crm.loading && !crm.ready" class="crm-state">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="crm.error" class="crm-state">
        <v-icon large color="error">mdi-cloud-alert-outline</v-icon>
        <div class="crm-state__title">{{ $t('crmLoadError') }}</div>
        <div class="crm-state__sub">{{ crm.error }}</div>
        <v-btn depressed color="primary" small @click="boot(true)">{{ $t('crmRetry') }}</v-btn>
      </div>
      <div v-else-if="crm.noWorkspace" class="crm-onboard">
        <div class="crm-onboard__card">
          <span class="crm-onboard__icon"><v-icon size="34" color="primary">mdi-rocket-launch-outline</v-icon></span>
          <h2>{{ $t('crmWelcome') }}</h2>
          <p>{{ $t('crmWelcomeHint') }}</p>
          <div class="crm-onboard__form">
            <v-text-field v-model="wsName" :label="$t('crmWorkspaceName')" outlined dense hide-details />
            <v-btn depressed color="primary" :loading="creating" :disabled="!wsName" class="app-form-action-btn" @click="createWorkspace">{{ $t('crmCreateWorkspace') }}</v-btn>
          </div>
          <div v-if="crm.invites.length" class="crm-onboard__invites">
            <div class="crm-onboard__label">{{ $t('crmMyInvites') }}</div>
            <div v-for="inv in crm.invites" :key="inv.token" class="crm-onboard__invite">
              <b>{{ inv.workspace && inv.workspace.name }}</b>
              <v-spacer />
              <v-btn small depressed color="primary" @click="acceptInvite(inv)">{{ $t('crmAccept') }}</v-btn>
              <v-btn small text @click="declineInvite(inv)">{{ $t('crmDecline') }}</v-btn>
            </div>
          </div>
        </div>
      </div>
      <template v-else-if="crm.ready">
        <leads-page v-if="section === 'leads'" :key="'l' + wsKey" />
        <activities-page v-else-if="section === 'activities' || section === 'mywork'" :key="section + wsKey" :mode="section" />
        <deals-page v-else :key="'d' + wsKey" />
      </template>
    </main>

    <!-- shared overlays -->
    <record-drawer
      :type="drawer.type"
      :record="drawer.record"
      :reload-key="drawer.reload"
      @close="drawer.record = null"
      @refreshed="r => drawer.record && Object.assign(drawer.record, r)"
      @edit="editRecord"
      @log="(kind, mode) => logActivity(drawer.type, drawer.record, kind, mode)"
      @convert="convertLead"
      @move="onDrawerMove"
      @action="onDrawerAction"
    />
    <lead-form v-model="leadForm.open" :lead="leadForm.lead" :prefill="leadForm.prefill" @saved="onSaved('lead', $event)" @open-lead="id => openRecord('lead', { id })" />
    <deal-form
      v-model="dealForm.open"
      :deal="dealForm.deal"
      :lead="dealForm.lead"
      :pipeline-id="dealForm.pipelineId"
      :stage-id="dealForm.stageId"
      @saved="onDealSaved"
    />
    <activity-dialog
      v-model="activity.open"
      :entity-type="activity.type"
      :record="activity.record"
      :default-type="activity.kind"
      :default-mode="activity.mode"
      @saved="onActivitySaved"
    />
    <move-dialog :state="moveState" />

    <v-dialog :value="!!askState" max-width="420" @input="v => !v && askState && askState.resolve(null)">
      <div v-if="askState" class="crm-ask">
        <div class="crm-ask__title">{{ askState.title }}</div>
        <v-text-field v-model="askState.value" :label="askState.label" outlined dense hide-details autofocus @keydown.enter="askState.value && askState.resolve(askState.value)" />
        <div class="crm-ask__foot">
          <v-btn depressed color="primary" class="app-form-action-btn" :disabled="!askState.value" @click="askState.resolve(askState.value)">{{ $t('crmConfirm') }}</v-btn>
          <v-btn outlined color="grey" class="app-form-action-btn" @click="askState.resolve(null)">{{ $t('crmCancel') }}</v-btn>
        </div>
      </div>
    </v-dialog>

    <transition name="crm-toast">
      <div v-if="toastState" class="crm-toast" :class="{ 'crm-toast--error': toastState.error }" role="status">
        <v-icon small color="#fff">{{ toastState.error ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
        <span>{{ toastState.text }}</span>
        <button v-if="toastState.undo" type="button" class="crm-toast__undo" @click="runUndo">{{ $t('crmUndo') }}</button>
        <button type="button" class="crm-toast__x" @click="toastState = null"><v-icon x-small color="#fff">mdi-close</v-icon></button>
      </div>
    </transition>
  </div>
</template>

<script>
import api, { crmError } from './crmApi'
import { crm, can, bootstrap, switchWorkspace } from './crmStore'
import LeadsPage from './leads/LeadsPage'
import DealsPage from './deals/DealsPage'
import RecordDrawer from './components/RecordDrawer'
import LeadForm from './components/LeadForm'
import DealForm from './components/DealForm'
import ActivityDialog from './components/ActivityDialog'
import MoveDialog from './components/MoveDialog'
import ActivitiesPage from './activities/ActivitiesPage'

export default {
  name: 'CrmShell',
  components: { LeadsPage, DealsPage, ActivitiesPage, RecordDrawer, LeadForm, DealForm, ActivityDialog, MoveDialog },
  provide () { return { shell: this } },
  data: () => ({
    wsName: '',
    creating: false,
    wsKey: 0,
    drawer: { type: 'deal', record: null, reload: 0 },
    leadForm: { open: false, lead: null, prefill: null },
    dealForm: { open: false, deal: null, lead: null, pipelineId: null, stageId: null },
    activity: { open: false, type: 'deal', id: null, record: null, kind: 'call', mode: 'done' },
    moveState: null,
    askState: null,
    toastState: null,
    toastTimer: null
  }),
  computed: {
    crm () { return crm },
    section () { return this.$route.meta.crmSection || 'deals' },
    workspace () { return crm.me && crm.me.workspace },
    tabs () {
      const t = []
      if (can('deals.view')) t.push({ key: 'deals', route: 'salesCrmDeals', icon: 'mdi-briefcase-outline', label: this.$t('crmDeals') })
      if (can('leads.view')) t.push({ key: 'leads', route: 'salesCrmLeads', icon: 'mdi-account-search-outline', label: this.$t('crmLeads') })
      if (can('activities.view')) t.push({ key: 'activities', route: 'salesCrmActivities', icon: 'mdi-calendar-check-outline', label: this.$t('crmActivities') })
      t.push({ key: 'mywork', route: 'salesCrmMyWork', icon: 'mdi-account-clock-outline', label: this.$t('crmMyWork') })
      return t
    }
  },
  created () { this.boot() },
  mounted () { window.addEventListener('keydown', this.onKey) },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKey)
    clearTimeout(this.toastTimer)
  },
  methods: {
    can,
    async boot (force) {
      await bootstrap(force)
      if (crm.ready && this.$route.name === 'salesCrm' && !can('deals.view') && can('leads.view')) {
        this.$router.replace({ name: 'salesCrmLeads' })
      }
    },
    onKey (e) {
      const tag = (e.target && e.target.tagName) || ''
      if (/input|textarea|select/i.test(tag) || (e.target && e.target.isContentEditable) || e.ctrlKey || e.metaKey || e.altKey) return
      if (!crm.ready || this.leadForm.open || this.dealForm.open || this.activity.open) return
      // e.code so the shortcut also works on the Persian keyboard layout
      if (e.code === 'KeyN' && can('leads.create')) { e.preventDefault(); this.newLead() }
      if (e.code === 'KeyD' && can('deals.create')) { e.preventDefault(); this.newDeal() }
      if (e.key === 'Escape' && this.drawer.record) this.drawer.record = null
    },
    async onSwitch (w) {
      if (this.workspace && w.id === this.workspace.id) return
      try {
        await switchWorkspace(w.id)
        this.drawer.record = null
        this.wsKey++
      } catch (e) { this.toast(crmError(e).message, { error: true }) }
    },
    async createWorkspace () {
      this.creating = true
      try {
        await api.createWorkspace({ name: this.wsName })
        await bootstrap(true)
      } catch (e) {
        this.toast(crmError(e).message, { error: true })
      } finally { this.creating = false }
    },
    async acceptInvite (inv) {
      try { await api.acceptInvite(inv.token); await bootstrap(true) } catch (e) { this.toast(crmError(e).message, { error: true }) }
    },
    async declineInvite (inv) {
      try { await api.declineInvite(inv.token); crm.invites = crm.invites.filter(i => i !== inv) } catch (e) { this.toast(crmError(e).message, { error: true }) }
    },

    // ---- API for child views (inject: ['shell']) ----
    toast (text, { undo = null, error = false } = {}) {
      clearTimeout(this.toastTimer)
      this.toastState = { text: text || (error ? this.$t('crmError') : ''), undo, error }
      this.toastTimer = setTimeout(() => { this.toastState = null }, undo ? 8000 : 4000)
    },
    async runUndo () {
      const fn = this.toastState && this.toastState.undo
      this.toastState = null
      if (!fn) return
      try { await fn(); this.toast(this.$t('crmUndone')) } catch (e) { this.toast(crmError(e).message, { error: true }) }
    },
    ask (title, label, value = '') {
      return new Promise(resolve => {
        this.askState = { title, label, value, resolve: v => { this.askState = null; resolve(v) } }
      })
    },
    openRecord (type, record) {
      this.drawer.type = type
      this.drawer.record = { ...record }
    },
    newLead (prefill = null) {
      this.leadForm = { open: true, lead: null, prefill }
    },
    newDeal ({ pipelineId = null, stageId = null } = {}) {
      this.dealForm = { open: true, deal: null, lead: null, pipelineId, stageId }
    },
    editRecord (r) {
      if (this.drawer.type === 'lead') this.leadForm = { open: true, lead: r, prefill: null }
      else this.dealForm = { open: true, deal: r, lead: null, pipelineId: r.pipeline_id, stageId: r.stage_id }
    },
    convertLead (lead) {
      this.dealForm = { open: true, deal: null, lead, pipelineId: null, stageId: null }
    },
    /** mode: 'done' (log what happened) | 'schedule' (plan a follow-up) */
    logActivity (type, record, kind = 'call', mode = 'done') {
      this.activity = { open: true, type, id: record.id, record, kind: kind || 'call', mode: mode || 'done' }
    },
    changed (type, record) {
      this.$emit('changed', { type, record })
      if (this.drawer.record && record && this.drawer.record.id === record.id && this.drawer.type === type) {
        Object.assign(this.drawer.record, record)
        this.drawer.reload++
      }
    },
    onSaved (type, rec) {
      this.toast(this.$t('crmSaved'))
      this.changed(type, rec)
    },
    onDealSaved (res) {
      if (this.dealForm.lead) {
        const lead = this.dealForm.lead
        this.toast(this.$t('crmConverted'))
        this.changed('lead', (res && res.lead) || lead)
        if (res && res.deal) {
          this.changed('deal', res.deal)
          this.openRecord('deal', res.deal)
        }
        return
      }
      const deal = res && res.deal ? res.deal : res
      this.toast(this.$t('crmSaved'))
      this.changed('deal', deal)
    },
    onActivitySaved () {
      this.toast(this.$t('crmActivitySaved'))
      if (this.drawer.record) this.drawer.reload++
      this.$emit('changed', { type: this.activity.type, record: { id: this.activity.id } })
    },

    /** Moves a deal to `stage`. Handles lost reason, entry conditions and
     *  required fields (doc §6.6). Resolves true when the server accepted. */
    async moveDeal (deal, stage, fromStage = null) {
      let payload = { stage_id: stage.id }
      if (stage.kind === 'lost' || stage.kind === 'won') {
        payload = await this.promptMove(deal, stage)
        if (!payload) return false
      }
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await api.moveDeal(deal.id, payload)
          const prevStage = fromStage || (deal.stage_id && { id: deal.stage_id })
          this.toast(this.$t('crmMovedTo', { to: stage.title }), {
            undo: prevStage && prevStage.id !== stage.id
              ? () => api.moveDeal(deal.id, { stage_id: prevStage.id }).then(() => this.changed('deal', { ...deal, stage_id: prevStage.id }))
              : null
          })
          this.changed('deal', { ...deal, stage_id: stage.id })
          return true
        } catch (e) {
          const err = crmError(e)
          if (err.code === 'required_fields') {
            const more = await this.promptMove(deal, stage, err.payload.missing_fields || [], err.message)
            if (!more) return false
            payload = { ...payload, ...more, custom_fields: { ...(payload.custom_fields || {}), ...(more.custom_fields || {}) } }
            continue
          }
          this.toast(err.message, { error: true })
          return false
        }
      }
      return false
    },
    promptMove (deal, stage, missing = null, message = '') {
      return new Promise(resolve => {
        this.moveState = { deal, stage, missing, message, resolve: p => { this.moveState = null; resolve(p) } }
      })
    },
    async onDrawerMove (deal, stage) {
      const ok = await this.moveDeal(deal, stage, { id: deal.stage_id })
      if (ok && this.drawer.record) {
        this.drawer.record.stage_id = stage.id
        if (stage.kind !== 'open') this.drawer.record.status = stage.kind
      }
    },
    async onDrawerAction (name, r) {
      const type = this.drawer.type
      try {
        if (name === 'copy') {
          const res = await api.copyDeal(r.id)
          this.toast(this.$t('crmCopied'))
          this.changed('deal', res)
        } else if (name === 'pause') {
          const reason = await this.ask(this.$t('crmPause'), this.$t('crmReason'))
          if (reason === null) return
          await api.pauseDeal(r.id, { reason })
          this.changed('deal', { ...r, status: 'paused' })
          this.toast(this.$t('crmSaved'), { undo: () => api.resumeDeal(r.id).then(() => this.changed('deal', { ...r, status: 'open' })) })
        } else if (name === 'resume') {
          await api.resumeDeal(r.id)
          this.changed('deal', { ...r, status: 'open' })
          this.toast(this.$t('crmSaved'))
        } else if (name === 'reopen') {
          const reason = await this.ask(this.$t('crmReopen'), this.$t('crmReason'))
          if (!reason) return
          await api.reopenDeal(r.id, { reason })
          this.changed('deal', { ...r, status: 'open' })
          this.toast(this.$t('crmSaved'))
        } else if (name === 'reject') {
          const reason = await this.ask(this.$t('crmRejectReason'), this.$t('crmReason'))
          if (!reason) return
          await api.rejectLead(r.id, { status: 'rejected', reason })
          this.changed('lead', { ...r, status: 'rejected' })
          this.toast(this.$t('crmSaved'), { undo: () => api.restoreLead(r.id).then(() => this.changed('lead', r)) })
        } else if (name === 'restore') {
          await api.restoreLead(r.id)
          this.changed('lead', r)
          this.toast(this.$t('crmSaved'))
        } else if (name === 'archive') {
          await api.archive(type, r.id)
          this.drawer.record = null
          this.changed(type, r)
          this.toast(this.$t('crmArchived'), { undo: () => api.unarchive(type, r.id).then(() => this.changed(type, r)) })
        } else if (name === 'delete') {
          const label = r.title || r.id
          const typed = await this.ask(this.$t('crmConfirmDelete', { name: label }), this.$t('crmTypeToConfirm'))
          if (!typed || String(typed).trim() !== String(label).trim()) {
            if (typed) this.toast(this.$t('crmConfirmMismatch'), { error: true })
            return
          }
          await api.deleteDeal(r.id)
          this.drawer.record = null
          this.changed('deal', r)
          this.toast(this.$t('crmDeleted'), { undo: () => api.undelete('deal', r.id).then(() => this.changed('deal', r)) })
        }
      } catch (e) {
        this.toast(crmError(e).message, { error: true })
      }
    }
  }
}
</script>

<style scoped>
.crm-page { display: flex; flex-direction: column; height: calc(100vh - 64px); min-height: 480px; padding: 14px 18px 12px; gap: 14px; background: #f3f5f9; overflow: hidden; }
.theme--dark .crm-page, .theme--dark.crm-page { background: #121212; }
.crm-page ::v-deep .v-input { height: auto !important; }
.crm-head { display: flex; align-items: center; gap: 18px; flex-shrink: 0; flex-wrap: wrap; }
.crm-head__brand { display: flex; align-items: center; gap: 10px; }
.crm-head__logo { width: 42px; height: 42px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; }
.crm-head__title { font-weight: 800; font-size: 1.05rem; line-height: 1.2; }
.crm-head__ws { display: inline-flex; align-items: center; gap: 6px; font-size: .76rem; opacity: .75; }
.crm-head__ws:hover { opacity: 1; }
.crm-head__ws-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.crm-head__tabs { display: flex; gap: 4px; padding: 4px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); }
.theme--dark .crm-head__tabs { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.crm-head__tab { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 16px; border-radius: 6px; font-size: .86rem; font-weight: 700; color: inherit !important; text-decoration: none; opacity: .65; transition: all .15s; }
.crm-head__tab.on { opacity: 1; background: rgba(25, 118, 210, .1); color: var(--v-primary-base) !important; }
.crm-head__tab.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.crm-head__add { border-radius: 6px !important; letter-spacing: 0; height: 38px !important; }
.crm-head__addmenu kbd { font-size: .65rem; padding: 0 5px; margin-inline-start: 16px; border-radius: 4px; background: rgba(127, 127, 127, .15); color: inherit; box-shadow: none; }
.crm-body { flex: 1; min-height: 0; }
.crm-state { height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.crm-state__title { font-weight: 700; }
.crm-state__sub { font-size: .8rem; opacity: .6; max-width: 420px; }
.crm-onboard { height: 100%; display: flex; align-items: center; justify-content: center; overflow-y: auto; }
.crm-onboard__card { width: 100%; max-width: 480px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; padding: 32px 28px; text-align: center; box-shadow: 0 1px 3px rgba(15, 23, 42, .05); }
.theme--dark .crm-onboard__card { background: #1e1e1e; }
.crm-onboard__icon { width: 72px; height: 72px; border-radius: 50%; background: rgba(25, 118, 210, .1); display: inline-flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.crm-onboard__card h2 { font-size: 1.2rem; margin-bottom: 6px; }
.crm-onboard__card p { font-size: .85rem; opacity: .65; }
.crm-onboard__form { display: flex; gap: 10px; margin-top: 18px; }
.crm-onboard__invites { margin-top: 24px; text-align: start; }
.crm-onboard__label { font-size: .76rem; opacity: .6; margin-bottom: 8px; }
.crm-onboard__invite { display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .08); margin-bottom: 6px; }
.crm-ask { background: #fff; border-radius: 6px; padding: 18px; }
.theme--dark .crm-ask { background: #1e1e1e; }
.crm-ask__title { font-weight: 700; margin-bottom: 16px; }
.crm-ask__foot { display: flex; gap: 10px; margin-top: 16px; }
.crm-ask ::v-deep fieldset { border-radius: 6px; }
.crm-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); z-index: 300; display: flex; align-items: center; gap: 10px; min-height: 44px; padding: 0 8px 0 16px; border-radius: 6px; background: #1f2937; color: #fff; font-size: .84rem; box-shadow: 0 10px 30px rgba(0, 0, 0, .25); max-width: calc(100vw - 32px); }
.crm-toast--error { background: #b03a2e; }
.crm-toast__undo { height: 30px; padding: 0 12px; border-radius: 6px; font-weight: 700; color: #8fc1ff; }
.crm-toast--error .crm-toast__undo { color: #fff; }
.crm-toast__undo:hover { background: rgba(255, 255, 255, .1); }
.crm-toast__x { width: 28px; height: 28px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
.crm-toast-enter-active, .crm-toast-leave-active { transition: all .2s; }
.crm-toast-enter, .crm-toast-leave-to { opacity: 0; transform: translate(-50%, 12px); }
.crm-page--mobile { padding: 10px 12px 8px; height: calc(100vh - 56px); gap: 10px; }
.crm-page--mobile .crm-head { gap: 10px; }
.crm-page--mobile .crm-head__tabs { order: 5; width: 100%; }
.crm-page--mobile .crm-head__tab { flex: 1; justify-content: center; }
.crm-page--mobile .crm-onboard__form { flex-direction: column; }
</style>
